import React, {useEffect, useState} from 'react';
import {Image, Platform, ScrollView, StyleSheet, View} from 'react-native';
import {Card, CheckBox} from '@rneui/themed';
import {observer} from 'mobx-react';

import Button from '../../components/themed/Button';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import ProductImage from '../../images/brand/giftCardsShop/product-image.png';
import {AppRoute} from '../../models/AppRoute';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {toAUD} from '../../utils/functions';
import {logger} from '../../utils/logger';
import {
  GIFT_CARD_DELIVERY_HELPER_TEXT,
  GIFT_CARD_DELIVERY_IMMEDIATE_TEXT,
  MAX_FORM_WIDTH,
} from '../constants';
import SignInRequiredModal from '../modals/SignInRequiredModal';
import {GiftCardShopRouteNavigatorScreenProps} from '../types/navigationTypes';

// --- Sub-components ---

const InvalidCartState = () => (
  <ContentView>
    <View style={{marginBottom: 16}}>
      <ActivityIndicator />
    </View>
    <Text align="center" variant="body">
      Invalid Cart State. Redirecting you to the Gift Card Shop...
    </Text>
  </ContentView>
);

const GiftCardPreview = ({item}: {item: OrderLine}) => (
  <View style={styles.giftCardPreview}>
    <View style={styles.giftCardImageContainer}>
      <Image source={ProductImage} style={styles.giftCardImage} resizeMode="contain" />
    </View>
    <View style={styles.giftCardInfo}>
      <Text variant="heading2" style={styles.giftCardPrice}>
        {toAUD(item.sell_price_cents)} Gift Card
      </Text>
      <Text variant="body" style={styles.giftCardMeta}>
        {config.APP_NAME}
      </Text>
    </View>
  </View>
);

const SectionLabel = ({children}: {children: string}) => (
  <Text variant="heading2" style={styles.sectionLabel}>
    {children}
  </Text>
);

const SectionValue = ({children}: {children: string}) => (
  <Text variant="body" style={styles.sectionValue}>
    {children}
  </Text>
);

const MessageCard = ({message}: {message: string}) => (
  <View style={styles.messageCard}>
    <Text variant="body" style={styles.messageText}>
      {message}
    </Text>
  </View>
);

const RecipientSection = ({name, message}: {name: string; message: string}) => (
  <View style={styles.recipientSection}>
    <View style={styles.fieldGroup}>
      <SectionLabel>RECIPIENT</SectionLabel>
      <SectionValue>{name}</SectionValue>
    </View>
    <View style={styles.fieldGroup}>
      <SectionLabel>YOUR MESSAGE</SectionLabel>
      <MessageCard message={message} />
    </View>
  </View>
);

const DeliveryRow = ({label, value}: {label: string; value: string}) => (
  <Text variant="body">
    <Text style={styles.deliveryLabel}>{label}: </Text>
    <Text style={styles.deliveryValue}>{value}</Text>
  </Text>
);

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const DeliverySection = ({email, mobile, date}: {email: string; mobile: string; date: Date}) => {
  const deliveryIsToday = isToday(date);
  const helperText = deliveryIsToday
    ? GIFT_CARD_DELIVERY_IMMEDIATE_TEXT
    : GIFT_CARD_DELIVERY_HELPER_TEXT;

  return (
    <View style={styles.deliverySection}>
      <SectionLabel>DELIVERY</SectionLabel>
      <View style={styles.deliveryRows}>
        <DeliveryRow label="Email" value={email} />
        <DeliveryRow label="Mobile" value={mobile} />
        <DeliveryRow label="Delivery date" value={date.toLocaleDateString()} />
      </View>
      <Text variant="caption" style={styles.deliveryHelper}>
        {helperText}
      </Text>
    </View>
  );
};

const TotalSection = ({price}: {price: number}) => (
  <View style={styles.totalSection}>
    <SectionLabel>TOTAL TODAY</SectionLabel>
    <Text variant="heading1" style={styles.totalPrice}>
      {toAUD(price)}
    </Text>
  </View>
);

type FooterActionsProps = {
  onEdit: () => void;
  isAgreeToTerms: boolean;
  onToggleTerms: () => void;
  onSubmit: () => void;
  onTermsPress: () => void;
  isSubmitDisabled: boolean;
};

const FooterActions = ({
  onEdit,
  isAgreeToTerms,
  onToggleTerms,
  onSubmit,
  onTermsPress,
  isSubmitDisabled,
}: FooterActionsProps) => (
  <View style={styles.footerActions}>
    <Button
      type="clear"
      title="Edit details"
      onPress={onEdit}
      titleStyle={styles.editLink}
      testID="gift-card-review-edit-button"
    />
    <CheckBox
      checked={isAgreeToTerms}
      onPress={onToggleTerms}
      iconType="material-icons"
      checkedIcon="check-box"
      uncheckedIcon="check-box-outline-blank"
      title={
        <Text variant="body">
          I agree to the{' '}
          <Text style={styles.termsLink} onPress={onTermsPress}>
            Terms & Conditions
          </Text>
        </Text>
      }
      containerStyle={styles.checkboxContainer}
      testID="gift-card-review-terms-checkbox"
    />
    <Button
      onPress={onSubmit}
      title="Confirm and Pay"
      containerStyle={styles.submitButtonContainer}
      buttonStyle={styles.submitButton}
      disabledStyle={styles.submitButtonDisabled}
      disabledTitleStyle={styles.submitButtonDisabledTitle}
      loading={false}
      disabled={isSubmitDisabled}
      testID="gift-card-review-confirm-button"
    />
  </View>
);

// --- Main Component ---

export const GiftCardStoreReviewScreen = observer(
  ({
    navigation: {navigate},
  }: GiftCardShopRouteNavigatorScreenProps<AppRoute.GIFT_CARDS_STORE_REVIEW>) => {
    const {cartStore, userStore} = useStores();
    const isLoggedIn = userStore.isLoggedIn;

    const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);
    const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);

    const {zenGiftCardRecipientFormValues} = cartStore;

    const handleEdit = () => navigate(AppRoute.GIFT_CARDS_HOME);
    const handleToggleAgreeToTerms = () => setIsAgreeToTerms(!isAgreeToTerms);
    const handleLoginModalBackdropPress = () => setIsLoginModalVisible(false);
    const handleLoginModalPress = () => {
      setIsLoginModalVisible(false);
      userStore.setPostLoginReturnToScreen(AppRoute.GIFT_CARDS_STORE_REVIEW);
      navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
    };
    const handleSubmit = async () => {
      if (isLoggedIn) {
        navigate(AppRoute.GIFT_CARDS_STORE_CHECKOUT);
      } else setIsLoginModalVisible(true);
    };
    const handleTermsPress = () => navigate(AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS);

    const inCartProducts = cartStore.currentOrder?.order_lines?.filter(
      (line: OrderLine) => !line.charge,
    );
    const invalidCartState = inCartProducts?.length !== 1 || !zenGiftCardRecipientFormValues;

    useEffect(() => {
      if (invalidCartState) navigate(AppRoute.GIFT_CARDS_HOME, {isResetForm: true});
    });

    if (invalidCartState) {
      logger.debug(
        'GiftCardStoreReviewScreen',
        "Invalid cart state. No 'in cart' products (or more than one) or no recipient form values.",
      );
      return <InvalidCartState />;
    }

    const giftCardItem = inCartProducts[0];
    const {note, recipientName, recipientEmail, recipientPhoneNumber, deliveryDate} =
      zenGiftCardRecipientFormValues;

    return (
      <ContentView style={styles.main} testID="gift-card-review-screen">
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <Card containerStyle={styles.cardContainer} wrapperStyle={styles.cardWrapper}>
            <GiftCardPreview item={giftCardItem} />

            <Card.Divider style={styles.divider} />

            <RecipientSection name={recipientName} message={note} />

            <Card.Divider style={styles.divider} />

            <DeliverySection
              email={recipientEmail}
              mobile={recipientPhoneNumber}
              date={deliveryDate}
            />

            <Card.Divider style={styles.divider} />

            <TotalSection price={giftCardItem.sell_price_cents} />
          </Card>

          <FooterActions
            onEdit={handleEdit}
            isAgreeToTerms={isAgreeToTerms}
            onToggleTerms={handleToggleAgreeToTerms}
            onSubmit={handleSubmit}
            onTermsPress={handleTermsPress}
            isSubmitDisabled={!isAgreeToTerms}
          />
        </ScrollView>

        <SignInRequiredModal
          isVisible={isLoginModalVisible}
          onPress={handleLoginModalPress}
          onBackdropPress={handleLoginModalBackdropPress}
        />
      </ContentView>
    );
  },
);

export default GiftCardStoreReviewScreen;

// --- Styles ---

const styles = StyleSheet.create({
  main: {
    maxWidth: MAX_FORM_WIDTH,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  cardContainer: {
    borderRadius: 12,
    padding: 16,
    margin: 0,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
      },
    }),
  },
  cardWrapper: {
    gap: 16,
  },
  divider: {
    marginVertical: 0,
  },

  // Gift Card Preview
  giftCardPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  giftCardImageContainer: {
    width: '35%',
    maxWidth: 120,
  },
  giftCardImage: {
    aspectRatio: 3 / 2,
    borderRadius: 6,
    ...Platform.select({
      native: {
        flex: 1,
        width: undefined,
        height: undefined,
      },
      default: {
        width: '100%',
      },
    }),
  },
  giftCardInfo: {
    flex: 1,
    flexShrink: 1,
    gap: 4,
  },
  giftCardPrice: {
    fontWeight: '700',
  },
  giftCardMeta: {
    color: '#666666',
  },

  // Section Labels & Values
  sectionLabel: {
    color: theme.colour.text.heading.primary,
    marginBottom: 4,
    fontSize: 12,
    fontWeight: '600',
  },
  sectionValue: {
    color: theme.colour.text.content.primary,
  },
  recipientSection: {
    gap: 16,
  },
  fieldGroup: {
    gap: 4,
  },

  // Message Card (neutral design - works with any theme)
  messageCard: {
    backgroundColor: '#F5F5F5',
    borderLeftWidth: 3,
    borderLeftColor: '#BDBDBD',
    borderRadius: 8,
    padding: 12,
    marginTop: 4,
  },
  messageText: {
    fontStyle: 'italic',
    color: '#424242',
    lineHeight: 22,
  },

  // Delivery Section
  deliverySection: {
    gap: 8,
  },
  deliveryRows: {
    gap: 4,
  },
  deliveryLabel: {
    color: '#666666',
  },
  deliveryValue: {
    color: theme.colour.text.content.primary,
  },
  deliveryHelper: {
    color: '#666666',
    marginTop: 4,
  },

  // Total Section
  totalSection: {
    gap: 4,
  },
  totalPrice: {
    color: theme.colour.text.content.primary,
    fontSize: 28,
    fontWeight: '700',
  },

  // Footer Actions
  footerActions: {
    marginTop: 16,
    alignItems: 'center',
    gap: 16,
  },
  editLink: {
    color: theme.colour.text.link.primary,
  },
  termsLink: {
    color: theme.colour.text.link.primary,
    textDecorationLine: 'underline',
  },
  checkboxContainer: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    padding: 0,
    margin: 0,
  },
  submitButtonContainer: {
    marginTop: 0,
  },
  submitButton: {
    height: 52,
    backgroundColor: theme.colour.background.button.primary,
  },
  submitButtonDisabled: {
    backgroundColor: theme.colour.background.button.primary,
    opacity: 0.6,
  },
  submitButtonDisabledTitle: {
    color: '#FFFFFF',
  },
});
